import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Stats from './components/Stats';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../../components/TableBox';
import { HeadCellType, TableContextProvider } from '../../../../interfaces/common';
import { buildHeadCells } from '../../../../utils/methods';
import { ListFilters } from '../../../../interfaces/vendor.interface';
import { GENERIC_LOOKUP_KEYS, VALIDATION_FIELD_MAPPING, HEAD_CELL_SORT_MAPPING } from '../../../../utils/appConstant';
import useSorting from '../../../../hooks/useSorting';
import { AuthRoutes, Modules, SortOrder } from '../../../../enums';
import usePagination from '../../../../hooks/usePagination';
import useUrlParams from '../../../../hooks/useUrlParams';
import useFetch from '../../../../hooks/useFetch';
import VendorService from '../../../../services/vendor.service';
import CommonService from '../../../../services/common.service';
import { usePersistentFilters } from '../../../../hooks/usePersistentFilters';

const List: React.FC = () => {
  const { t } = useTranslation();

  const defaultFilters: ListFilters = {
    searchText: '',
    serviceTypeId: '',
    status: '1',
    cities: [],
  };

  const { initialFilters, saveFilters, resetFilters } = usePersistentFilters<ListFilters>('filters_vendors', defaultFilters);

  // Initialize list state with persisted filters
  const [filters, setFilters] = useState<ListFilters>(initialFilters);

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    serviceTypeId: '',
    status: '',
    cities: '',
  });

  const headCells: HeadCellType[] = buildHeadCells([
    VALIDATION_FIELD_MAPPING.name,
    VALIDATION_FIELD_MAPPING.phoneNumber,
    VALIDATION_FIELD_MAPPING.serviceType,
    VALIDATION_FIELD_MAPPING.cities,
    VALIDATION_FIELD_MAPPING.status,
    VALIDATION_FIELD_MAPPING.actions,
  ], 'vendorList');

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const {
    order, orderBy, setOrder, setOrderBy,
  } = useSorting({
    defaultOrder: searchParams.get('order') as SortOrder || SortOrder.asc,
    defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.name
  });

  const {
    page, perPage, setPage, setPerPage,
  } = usePagination({
    defaultPage: Number(searchParams.get('page')) || 1
  });

  useUrlParams({
    type: AuthRoutes.vendors, uri: Modules.resources + AuthRoutes.vendors, perPage, page, order, orderBy
  });

  const { total, data: vendors, loading } = useFetch({
    service: VendorService,
    func: 'list',
    payload: {
      page, perPage,
      orderBy: (HEAD_CELL_SORT_MAPPING.vendorList as any)[orderBy] || orderBy,
      order,
      searchText: filters.searchText.trim(),
      serviceTypeId: filters.serviceTypeId,
      status: ((filters.status === '1') ? 1 : (filters.status === '0' ? 0 : "")),
      cities: filters.cities,
    },
    dependency: [order, orderBy, page, perPage, filters.searchText, filters.status, filters.serviceTypeId, filters.cities]
  });

  const { data: kpis } = useFetch({
    service: VendorService,
    func: 'kpis',
    payload: null,
    dependency: []
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: '' },
    dependency: []
  });

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: dropdownSearchData.cities },
    dependency: [dropdownSearchData.cities]
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: '' },
    dependency: []
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status]
  });

  const { data: allVendorTypes } = useFetch({
    service: VendorService,
    func: 'serviceTypesLookup',
    payload: { searchText: '' },
    dependency: []
  });

  const { data: serviceTypes } = useFetch({
    service: VendorService,
    func: 'serviceTypesLookup',
    payload: { searchText: dropdownSearchData.serviceTypeId },
    dependency: [dropdownSearchData.serviceTypeId]
  });

  const handleApplyFilters = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setFilters(newFilters);
    saveFilters(newFilters);
  }, [saveFilters, setPage]);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setFilters(defaultFilters);
    resetFilters();
  }, [resetFilters, setPage]);

  const handleDropdownSearchInputChange = useCallback((event: any, name: string) => {
    setDropdownSearchData((prev: any) => {
      return {
        ...prev,
        [name]: event.target.value,
      };
    });
  }, [setDropdownSearchData]);

  const headerSection = <TableHeaderSection total={total} />

  const values: TableContextProvider = useMemo(() => {
    return {
      headerSection,
      row: Row,
      data: vendors || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t("vendor.list.pageTexts.table.noDataFoundMessage"),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy
    };
  }, [headCells, order, orderBy, page, perPage, total, loading, vendors, setPage, setPerPage, setOrder, setOrderBy]);

  return (
    <>
      <Header />

      <Stats data={kpis} />

      <Filters
        serviceTypes={serviceTypes}
        allVendorTypes={allVendorTypes}
        status={status}
        allStatus={allStatus}
        allCities={allCities}
        cities={cities}
        dropdownSearchData={dropdownSearchData}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        initialFilters={initialFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      <TableBox tableValues={values} />
    </>
  );
};

export default List;
